import { deleteAgent } from '@/services/api';
import { ImageType } from '@d-id/types';
import { debounce } from '@mui/material';
import { useEffect, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { CAMPAIGN_DRAFT_AGENT_ID } from '../constants';
import { useSaveCampaignAgent } from './useSaveCampaignAgent';

/**
 * Custom hook for managing campaign agent data.
 * This hook handles saving the campaign agent and updating the form context.
 * The agent id is stored in local storage so that it can be deleted when the campaign is wasn't saved.
 * If the agent id changes, the previous agent is deleted.
 * @returns An object containing the `isCreatingAgent` boolean flag.
 */
export const useCampaignAgent = () => {
    const { mutateAsync, isLoading: isCreatingAgent } = useSaveCampaignAgent();
    const { watch, register, setValue } = useFormContext();
    const controllerRef = useRef<AbortController | null>(null);

    const [presenter, voice, agent, isStitch, campaignId] = watch(['presenter', 'voice', 'agent', 'is_stitch', 'id']);
    const agentId = agent?.id;

    useEffect(() => {
        register('agent');
    }, [register]);

    useEffect(() => {
        const saveAgent = debounce(async () => {
            if (!presenter || presenter.presenterType === ImageType.Skeleton || !voice) {
                return;
            }

            controllerRef.current?.abort();
            const nextController = new AbortController();
            controllerRef.current = nextController;

            const agent = await mutateAsync({
                agentId,
                presenter,
                voice,
                isStitch,
                controller: nextController,
                campaignId,
            });

            setValue('agent', agent);

            if (agent.id === agentId) {
                return;
            }

            if (agentId) {
                deleteAgent(agentId);
            }

            localStorage.setItem(CAMPAIGN_DRAFT_AGENT_ID, agent.id || '');
        }, 1000);

        saveAgent();
    }, [agentId, isStitch, presenter, voice]);

    return { isCreatingAgent };
};
